import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Typography,
} from "@mui/material";
import RiskAndCoverageStyles from "./RiskAndCoverageStyles";
import { useTranslation } from "react-i18next";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { useParams } from "react-router-dom";
import { GetPolicyRisks } from "../../../../store/user/policyDetails/policyDetailsActions";
import { currencyFormat } from "../../../../utils/Utility";
import { getRiskSpecificFields } from "./RiskFieldRenderer";
import { RootState } from "../../../../store/store";
import CustomTable from "../../../../common/CustomTable/CustomTable";
import CustomDrawer from "../../../../common/CustomDrawer/CustomDrawer";
import IconButton from "@mui/material/IconButton";
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputBase from '@mui/material/InputBase';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { IColumn } from "../../../../common/CustomTable/CustomTable";

// Move getAllFORisks above its first usage
const getAllFORisks = (item: any) => {
  const allRisks = [];
  if (item?.ownerOccupiedDwellingRisks?.length) {
    allRisks.push(...item.ownerOccupiedDwellingRisks);
  }
  if (item?.contentsOnlyRisks?.length) {
    allRisks.push(...item.contentsOnlyRisks);
  }
  if (item?.additionalDwellingRisks?.length) {
    allRisks.push(...item.additionalDwellingRisks);
  }
  if (item?.barnBuildingStructureRisks?.length) {
    allRisks.push(...item.barnBuildingStructureRisks);
  }
  if (item?.farmPersonalPropertyRisks?.length) {
    allRisks.push(...item.farmPersonalPropertyRisks);
  }
  if (item?.unitOwnersRisks?.length) {
    allRisks.push(...item.unitOwnersRisks);
  }
  return allRisks;
};

const RiskAndCoverage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { policyId, carrier, system, lobCode } = useParams();
  const { liability } = useAppSelector((state: any) => state.policyDetails);
  const { message } = useAppSelector((state: RootState) => state.common);
  console.log("Data", liability)

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState<any>(null);
  const [drawerTab, setDrawerTab] = useState(0);
  const [drawerLob, setDrawerLob] = useState('Property');
  // FO Drawer: selected location index and risk index
  const [drawerFoLocationIdx, setDrawerFoLocationIdx] = useState<number>(0);
  const [drawerFoRiskIdx, setDrawerFoRiskIdx] = useState<number>(0);
  // Track expanded accordions
  const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(new Set());

  // Get all risks for dropdown
  const propertyRisks = liability?.propertyList?.flatMap((loc: any) => loc.policyRiskList || []) || [];
  const liabilityRisks = liability?.liabilityList?.flatMap((loc: any) => loc.policyRiskList || []) || [];
  const riskList = drawerLob === 'Property' ? propertyRisks : liabilityRisks;
  // Risk name dropdown value
  const [drawerRisk, setDrawerRisk] = useState<any>(null);

  // FO: get all locations
  const foLocations = Array.isArray(liability?.riskLocations) ? liability.riskLocations : [];

  // Handle accordion expansion
  const handleAccordionChange = (accordionId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    const newExpanded = new Set(expandedAccordions);
    if (isExpanded) {
      newExpanded.add(accordionId);
    } else {
      newExpanded.delete(accordionId);
    }
    setExpandedAccordions(newExpanded);
  };

  // Check if accordion is expanded
  const isAccordionExpanded = (accordionId: string) => expandedAccordions.has(accordionId);

  // When drawer opens or LOB changes, set default risk
  React.useEffect(() => {
    if (drawerOpen) {
      if (liability?.lobCode !== 'FO') {
        setDrawerLob('Property');
        setDrawerRisk(propertyRisks[0] || null);
      }
    }
    // eslint-disable-next-line
  }, [drawerOpen]);

  React.useEffect(() => {
    if (drawerLob === 'Property') {
      setDrawerRisk(propertyRisks[0] || null);
    } else {
      setDrawerRisk(liabilityRisks[0] || null);
    }
  }, [drawerLob]);

  // When risk changes, update selectedRisk (non-FO)
  React.useEffect(() => {
    if (drawerRisk && liability?.lobCode !== 'FO') setSelectedRisk(drawerRisk);
  }, [drawerRisk, liability?.lobCode]);

  // When FO location or risk index changes, update selectedRisk
  React.useEffect(() => {
    if (liability?.lobCode === 'FO') {
      const risks = foLocations[drawerFoLocationIdx] ? getAllFORisks(foLocations[drawerFoLocationIdx]) : [];
      setSelectedRisk(risks[drawerFoRiskIdx] || null);
    }
    // eslint-disable-next-line
  }, [drawerFoLocationIdx, drawerFoRiskIdx, liability?.lobCode]);

  const getRiskCount = (item: any) => {
    if (!item) return 0;
    if (liability?.lobCode === "FO") {
      return (
        (item?.ownerOccupiedDwellingRisks?.length || 0) +
        (item?.contentsOnlyRisks?.length || 0) +
        (item?.additionalDwellingRisks?.length || 0) +
        (item?.barnBuildingStructureRisks?.length || 0) +
        (item?.farmPersonalPropertyRisks?.length || 0) +
        (item?.unitOwnersRisks?.length || 0)
      );
    } else {
      return item?.policyRiskList?.length || 0;
    }
  };

function calculateTotalPremium(data:any): number {
  let total = 0;

  // Add liabilityList[].finalPremiumAmount
  if (Array.isArray(data.liabilityList)) {
    data.liabilityList.forEach((liability: any) => {
      total += parseFloat(liability.finalPremiumAmount || '0');
    });
  }

  // Add propertyList[].finalPremiumAmount
  if (Array.isArray(data.propertyList)) {
    data.propertyList.forEach((property: any) => {
      total += parseFloat(property.finalPremiumAmount || '0');
    });
  }

  return total;
}
function calculateTotalCoverage(data: any): number {
    let totalCover = 0;
  
    // From liabilityList → policyRiskList → policyCoverageList
    if (Array.isArray(data.liabilityList)) {
      data.liabilityList.forEach((liability: any) => {
        if (Array.isArray(liability.policyRiskList)) {
          liability.policyRiskList.forEach((risk: any) => {
            if (Array.isArray(risk.policyCoverageList)) {
              risk.policyCoverageList.forEach((coverage: any) => {
                totalCover += parseFloat(coverage.covPremiumAmt || '0');
              });
            }
          });
        }
      });
    }
  
    // From propertyList → policyRiskList → policyCoverageList
    if (Array.isArray(data.propertyList)) {
      data.propertyList.forEach((property: any) => {
        if (Array.isArray(property.policyRiskList)) {
          property.policyRiskList.forEach((risk: any) => {
            if (Array.isArray(risk.policyCoverageList)) {
              risk.policyCoverageList.forEach((coverage: any) => {
                totalCover += parseFloat(coverage.covPremiumAmt || '0');
              });
            }
          });
        }
      });
    }
  
    return totalCover;
  }
 
  useEffect(() => {
    if (policyId && carrier && system && lobCode) {
      dispatch(GetPolicyRisks({ policyId, carrier, system, lobCode }));
    }
  }, [policyId, dispatch, carrier, system, lobCode]);

  // Helper for Liability fields
  function renderLiabilityFields(risk: any) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 3 }}>
        <Box>
          <Typography sx={{  fontSize: 16 }}>{risk.businessCategory || '-'}</Typography>
          <Typography sx={{ color: '#888', fontSize: 14 }}>{t('Business Category')}</Typography>
        </Box>
        <Box>
          <Typography sx={{  fontSize: 16 }}>{risk.businessClass || '-'}</Typography>
          <Typography sx={{ color: '#888', fontSize: 13 }}>{t('Class')}</Typography>
        </Box>
        <Box>
          <Typography sx={{  fontSize: 16 }}>{risk.glClassCode || '-'}</Typography>
          <Typography sx={{ color: '#888', fontSize: 13 }}>{t('GL Class Code')}</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 16 }}>{risk.exposureBasis || '-'}</Typography>
          <Typography sx={{ color: '#888', fontSize: 13 }}>{t('Exposure Basis')}</Typography>
        </Box>
      </Box>
    );
  }

  // Drawer content component
  const renderDrawerContent = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header Controls */}
      <Box sx={RiskAndCoverageStyles.drawerHeaderBox}>
        {liability?.lobCode === 'FO' ? (
          <>
            <Typography sx={RiskAndCoverageStyles.drawerHeaderLabel}>{t('Risks on :')}</Typography>
            {/* Only RiskType dropdown for selected location */}
            {(() => {
              const currentFoRisks = foLocations[drawerFoLocationIdx] ? getAllFORisks(foLocations[drawerFoLocationIdx]) : [];
              return (
                <Select
                  value={drawerFoRiskIdx}
                  onChange={e => setDrawerFoRiskIdx(Number(e.target.value))}
                  input={<InputBase />}
                  sx={RiskAndCoverageStyles.drawerSelect}
                  MenuProps={{ disablePortal: true }}
                >
                  {currentFoRisks.map((risk: any, idx: number) => (
                    <MenuItem value={idx} key={idx}>{risk.riskType || risk.description || '--'}</MenuItem>
                  ))}
                </Select>
              );
            })()}
          </>
        ) : (
          <>
            <Typography sx={RiskAndCoverageStyles.drawerHeaderLabel}>
              {t('Risks on')}
            </Typography>
            {/* Only show LOB selector if not FO */}
            {liability?.lobCode !== 'FO' && (
              <Select
                value={drawerLob}
                onChange={e => setDrawerLob(e.target.value)}
                input={<InputBase />}
                sx={RiskAndCoverageStyles.drawerSelectLob}
                MenuProps={{ disablePortal: true }}
              >
                <MenuItem value="Property">{t('Property')}</MenuItem>
                <MenuItem value="Liability">{t('Liability')}</MenuItem>
              </Select>
            )}
            {/* Only show risk description dropdown if not FO */}
            {liability?.lobCode !== 'FO' && (
              <Select
                value={drawerRisk?.description || ''}
                onChange={e => {
                  const found = riskList.find((r: any) => r.description === e.target.value);
                  setDrawerRisk(found || null);
                }}
                input={<InputBase />}
                sx={RiskAndCoverageStyles.drawerSelectRisk}
                MenuProps={{ disablePortal: true }}
              >
                {riskList.map((risk: any, idx: number) => (
                  <MenuItem value={risk.description} key={idx}>{risk.description}</MenuItem>
                ))}
              </Select>
            )}
          </>
        )}
      </Box>

      {/* Tabs and Total Premium */}
      <Box sx={RiskAndCoverageStyles.drawerTabsBox}>
        <Box sx={RiskAndCoverageStyles.drawerTabsContainer}>
          <Tabs
            value={drawerTab}
            onChange={(_, v) => setDrawerTab(v)}
            sx={RiskAndCoverageStyles.drawerTabs}
            TabIndicatorProps={{ sx: RiskAndCoverageStyles.drawerTabIndicator }}
          >
            <Tab label={t('Risk')} sx={RiskAndCoverageStyles.drawerTab} />
            <Tab label={t('Coverage & Limits')} sx={RiskAndCoverageStyles.drawerTabCoverage} />
          </Tabs>
          <Box sx={RiskAndCoverageStyles.flex1} />
          <Typography sx={RiskAndCoverageStyles.drawerTotalPremiumLabel}>
            {t('Total Premium:')} <b style={{ color: '#222' }}>{currencyFormat('us', selectedRisk?.finalPremiumAmount)}</b>
          </Typography>
        </Box>
      </Box>

      {/* Tab Content */}
      <Box sx={RiskAndCoverageStyles.drawerTabContentBox}>
        {drawerTab === 0 && selectedRisk && (
          <Box>
            {drawerLob === 'Liability'
              ? renderLiabilityFields(selectedRisk)
              : <Box sx={RiskAndCoverageStyles.riskSpecificFieldsBox}>{getRiskSpecificFields(selectedRisk, liability?.lobCode, t)}</Box>
            }
          </Box>
        )}
        {drawerTab === 1 && selectedRisk && (
          <Box>
            {/* Header row */}
            <Box sx={RiskAndCoverageStyles.coverageHeaderRow}>
              <Typography sx={RiskAndCoverageStyles.coverageHeaderLabel}>{t('Coverages')}</Typography>
              <Typography sx={RiskAndCoverageStyles.coverageHeaderLabelLimit}>{t('Limit')}</Typography>
            </Box>
            {/* Bottom horizontal line */}
            <Box sx={RiskAndCoverageStyles.coverageHeaderDivider} />
            {/* Coverage rows */}
            <Box sx={RiskAndCoverageStyles.coverageRowsBox}>
              {selectedRisk.policyCoverageList?.map((coverage: any, idx: number) => (
                <React.Fragment key={idx}>
                  <Box sx={RiskAndCoverageStyles.coverageRow}>
                    <Box sx={RiskAndCoverageStyles.coverageNameBox}>
                      <Typography sx={RiskAndCoverageStyles.coverageNameText}>
                        {coverage.coverageName}
                      </Typography>
                      <Typography sx={RiskAndCoverageStyles.coverageTypeText}>
                        {coverage?.limits.length > 1 ? t('On-Premises /Off-Premises') : ''}
                      </Typography>
                    </Box>
                    <Box sx={RiskAndCoverageStyles.mobileLimitContainer}>
                      {/* Mobile view: Show each limit on separate line */}
                      {coverage?.limits?.length > 1 ? (
                        coverage.limits
                          .map((limit: any, limitIdx: number) => {
                            const limitValue = parseFloat(limit.value);
                            return !isNaN(limitValue) ? (
                              <React.Fragment key={limitIdx}>
                                <Typography 
                                  sx={{
                                    ...RiskAndCoverageStyles.coverageLimitText,
                                    ...RiskAndCoverageStyles.mobileLimitText
                                  }}
                                >
                                  {currencyFormat('us', limitValue)}{limitIdx < coverage.limits.length - 1 ? '/' : ''}
                                </Typography>
                              </React.Fragment>
                            ) : null;
                          })
                          .filter((val: any) => val !== null)
                      ) : coverage?.limits?.[0]?.value !== undefined && !isNaN(parseFloat(coverage.limits[0].value)) ? (
                        <Typography 
                          sx={{
                            ...RiskAndCoverageStyles.coverageLimitText,
                            ...RiskAndCoverageStyles.singleLimitMobileText
                          }}
                        >
                          {currencyFormat('us', parseFloat(coverage.limits[0].value))}
                        </Typography>
                      ) : (
                        <Typography 
                          sx={{
                            ...RiskAndCoverageStyles.coverageLimitText,
                            ...RiskAndCoverageStyles.singleLimitMobileText
                          }}
                        >
                          --
                        </Typography>
                      )}
                      
                      {/* Desktop view: Show joined limits or single limit */}
                      <Typography sx={{
                        ...RiskAndCoverageStyles.coverageLimitText,
                        ...RiskAndCoverageStyles.desktopLimitText
                      }}>
                        {coverage?.limits?.length > 1
                          ? coverage.limits
                              .map((limit: any) => {
                                const limitValue = parseFloat(limit.value);
                                return !isNaN(limitValue)
                                  ? currencyFormat('us', limitValue)
                                  : null;
                              })
                              .filter((val: any) => val !== null)
                              .join(' / ') || '--'
                          : coverage?.limits?.[0]?.value !== undefined && !isNaN(parseFloat(coverage.limits[0].value))
                          ? `${currencyFormat('us', parseFloat(coverage.limits[0].value))}`
                          : '--'}
                      </Typography>
                    </Box>
                  </Box>
                  {/* Faded divider below each row */}
                  <Box sx={RiskAndCoverageStyles.coverageRowDivider} />
                </React.Fragment>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <Box sx={RiskAndCoverageStyles.summaryContainer}>
        <Box sx={RiskAndCoverageStyles.summaryBox}>
          <Typography sx={RiskAndCoverageStyles.summaryValue}>
            {liability?.lobCode === 'FO'
              ? currencyFormat('us', liability?.finalPremiumAmount || 0)
              : currencyFormat('us', calculateTotalPremium(liability))}
          </Typography>
          <Typography sx={RiskAndCoverageStyles.summaryLabel}>{t('Total Premium')}</Typography>
        </Box>
        {liability?.lobCode !== 'BOP' && (
          <Box sx={RiskAndCoverageStyles.summaryBox}>
            <Typography sx={RiskAndCoverageStyles.summaryValue}>
              {liability?.lobCode === 'FO'
                ? currencyFormat('us', (
                    Array.isArray(liability?.riskLocations)
                      ? liability.riskLocations.reduce((total: number, loc: any) => {
                          const risks = getAllFORisks(loc);
                          return (
                            total +
                            risks.reduce((riskSum: number, risk: any) => {
                              if (Array.isArray(risk.policyCoverageList)) {
                                return (
                                  riskSum +
                                  risk.policyCoverageList.reduce((covSum: number, cov: any) => covSum + parseFloat(cov.covPremiumAmt || '0'), 0)
                                );
                              }
                              return riskSum;
                            }, 0)
                          );
                        }, 0)
                      : 0
                  ))
                : currencyFormat('us', calculateTotalCoverage(liability))}
            </Typography>
            <Typography sx={RiskAndCoverageStyles.summaryLabel}>{t('Total Coverage')}</Typography>
          </Box>
        )}
      </Box>
      <Box sx={RiskAndCoverageStyles.pagePaddingBox}>
        {/* FO logic: Use riskLocations and getAllFORisks */}
        {liability && liability.lobCode === "FO" && Array.isArray(liability.riskLocations) && liability.riskLocations.length > 0 ? (
          liability.riskLocations.map((liabilityItem: any, index: number) => (
            <Accordion
              key={`fo-location-accordion-${index}`}
              sx={RiskAndCoverageStyles.locationAccordion}
              onChange={handleAccordionChange(`fo-location-accordion-${index}`)}
            >
              <AccordionSummary
                expandIcon={<ArrowDropDownIcon sx={RiskAndCoverageStyles.expandIcon} />}
              >
                <Box sx={RiskAndCoverageStyles.locationHeaderBox}>
                  <Box sx={RiskAndCoverageStyles.locationLeftBox}>
                    <PlaceOutlinedIcon sx={RiskAndCoverageStyles.placedIcon} />
                    <Box>
                      <Typography sx={RiskAndCoverageStyles.locationLabel}>{t('Location')}</Typography>
                      <Typography sx={RiskAndCoverageStyles.locationTitle}>
                        {liabilityItem?.address?.addr1 || `Location ${index + 1}`}
                      </Typography>
                      <Typography sx={RiskAndCoverageStyles.locationAddress}>
                        {[
                          liabilityItem?.address?.city,
                          liabilityItem?.address?.stateProvCd,
                          liabilityItem?.address?.county,
                          liabilityItem?.address?.postalCode,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={RiskAndCoverageStyles.riskCountBox}>
                    <Typography sx={RiskAndCoverageStyles.riskCountLabel}>{t('Total Risks')}</Typography>
                    <Typography sx={RiskAndCoverageStyles.riskCountText}>
                      {getRiskCount(liabilityItem).toString().padStart(2, '0')}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      '@media (max-width:430px)': { display: 'none' },
                      display: 'block',
                    }}
                  >
                    <Typography style={{textAlign:"right"}}>{isAccordionExpanded(`fo-location-accordion-${index}`) ? t('Hide Risks') : t('Show Risks')}</Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {/* --- BEGIN: FO Risk Summary Table --- */}
                {(() => {
                  const risks = getAllFORisks(liabilityItem);
                  // Prepare table rows
                  const rows = risks.map((policyRisk: any, rowIdx: number) => {
                    // Premium: Use finalPremiumAmount from the risk itself
                    let premium = parseFloat(policyRisk.finalPremiumAmount || '0');
                    // Coverage: Sum covPremiumAmt from policyCoverageList
                    let coverage = 0;
                    if (Array.isArray(policyRisk.policyCoverageList)) {
                      coverage = policyRisk.policyCoverageList.reduce((sum: number, coverage: any) => {
                        return sum + parseFloat(coverage.covPremiumAmt || '0');
                      }, 0);
                    }
                    return {
                      riskName: policyRisk.riskType || policyRisk.description || '-',
                      premium,
                      coverage,
                      _policyRisk: policyRisk, // for details icon
                      _rowIndex: rowIdx,
                    };
                  });
                  // Calculate totals
                  const total = rows.reduce(
                    (acc: { premium: number; coverage: number }, row: any) => {
                      acc.premium += row.premium;
                      acc.coverage += row.coverage;
                      return acc;
                    },
                    { premium: 0, coverage: 0 }
                  );
                  // Add total row
                  rows.push({
                    riskName: t('Total'),
                    premium: total.premium,
                    coverage: total.coverage,
                    _policyRisk: null,
                    _rowIndex: -1,
                  });
                  // Table columns
                  const columns: IColumn[] = [
                    { 
                      label: t('Risk Name'), 
                      accessorKey: 'riskName',
                      styles: { textAlign: 'left' as const },
                      Cell: (row: any) => (
                        <Box sx={RiskAndCoverageStyles.riskNameCellBox}>
                          <span>{row.riskName}</span>
                        </Box>
                      )
                    },
                    { 
                      label: t('Premium'), 
                      accessorKey: 'premium', 
                      styles: { textAlign: 'right' as const },
                      Cell: (row: any) => (
                        <Box sx={RiskAndCoverageStyles.rightAlignedCell}>
                          {row.riskName === t('Total') ? <b>{currencyFormat('us', row.premium)}</b> : currencyFormat('us', row.premium)}
                        </Box>
                      )
                    },
                    { 
                      label: t('Coverage'), 
                      accessorKey: 'coverage', 
                      styles: { textAlign: 'right' as const },
                      Cell: (row: any) => (
                        <Box sx={RiskAndCoverageStyles.rightAlignedCell}>
                          {row.riskName === t('Total') ? <b>{currencyFormat('us', row.coverage)}</b> : currencyFormat('us', row.coverage)}
                        </Box>
                      )
                    },
                    { label: '', accessorKey: 'details',
                      Cell: (row: any, opts?: { rowIndex: number; isHovered: boolean }) => (
                        row.riskName !== t('Total') ? (
                          <IconButton
                            size="small"
                            sx={opts?.isHovered ? RiskAndCoverageStyles.detailsIconButtonVisible : RiskAndCoverageStyles.detailsIconButtonHidden}
                            onClick={e => {
                              e.stopPropagation();
                              setDrawerFoLocationIdx(index); // set location index
                              setDrawerFoRiskIdx(row._rowIndex); // set risk index
                              setSelectedRisk(getAllFORisks(liability.riskLocations[index])[row._rowIndex] || null); // ensure correct risk
                              setDrawerOpen(true);
                            }}
                            tabIndex={-1}
                          >
                            <ArticleOutlinedIcon fontSize="small" />
                          </IconButton>
                        ) : null
                      )
                    },
                  ];
                  // Render the table using the prepared rows and columns
                  return (
                    <Box sx={RiskAndCoverageStyles.tableContainerBox}>
                      <CustomTable columns={columns} data={rows} pagination={false} stickyHeader={true} />
                    </Box>
                  );
                })()}
                {/* --- END: FO Risk Summary Table --- */}
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          // Non-FO logic (existing)
          ((Array.isArray(liability?.propertyList) && liability.propertyList.length > 0) ||
            (Array.isArray(liability?.liabilityList) && liability.liabilityList.length > 0)) && (
            <>
              {(liability.propertyList || liability.liabilityList).map((liabilityItem: any, index: number) => (
                <Accordion
                  key={`liability-location-accordion-${index}`}
                  sx={RiskAndCoverageStyles.locationAccordion}
                  onChange={handleAccordionChange(`liability-location-accordion-${index}`)}
                >
                  <AccordionSummary
                    expandIcon={<ArrowDropDownIcon sx={RiskAndCoverageStyles.expandIcon} />}
                  >
                     <Box sx={RiskAndCoverageStyles.locationHeaderBox}>
                      <Box sx={RiskAndCoverageStyles.locationLeftBox}>
                        <PlaceOutlinedIcon sx={RiskAndCoverageStyles.placedIcon} />
                        <Box>
                          <Typography sx={RiskAndCoverageStyles.locationLabel}>{t('Location')}</Typography>
                          <Typography sx={RiskAndCoverageStyles.locationTitle}>
                            {liabilityItem?.address?.addr1 || "-"}
                          </Typography>
                          <Typography sx={RiskAndCoverageStyles.locationAddress}>
                            {[
                              liabilityItem?.address?.city,
                              liabilityItem?.address?.stateProvCd,
                              liabilityItem?.address?.county,
                              liabilityItem?.address?.postalCode,
                            ]
                              .filter(Boolean)
                              .join(", ")}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={RiskAndCoverageStyles.riskCountBox}>
                        <Typography sx={RiskAndCoverageStyles.riskCountLabel}>{t('Total Risks')}</Typography>
                        <Typography sx={RiskAndCoverageStyles.riskCountText}>
                          {liabilityItem?.policyRiskList?.length?.toString().padStart(2, '0') || "00"}
                        </Typography>
                      </Box>
                      <Box
                    sx={{
                      '@media (max-width:430px)': { display: 'none' },
                      display: 'block',
                    }}
                  >
                    <Typography style={{textAlign:"right"}}>{isAccordionExpanded(`liability-location-accordion-${index}`) ? t('Hide Risks') : t('Show Risks')}</Typography>
                  </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {/* --- BEGIN: Risk Summary Table --- */}
                    {Array.isArray(liabilityItem?.policyRiskList) && liabilityItem.policyRiskList.length > 0 && (
                      (() => {
                        // Table Data Preparation
                        // Prepare table rows
                        const rows = liabilityItem.policyRiskList.map((policyRisk: any, rowIdx: number) => {
                          // Property Premium: Use finalPremiumAmount from the risk itself
                          let propertyPremium = parseFloat(policyRisk.finalPremiumAmount || '0');
                          let propertyCoverage = 0;
                          if (Array.isArray(policyRisk.policyCoverageList)) {
                            propertyCoverage = policyRisk.policyCoverageList.reduce((sum: number, coverage: any) => {
                              return sum + parseFloat(coverage.covPremiumAmt || '0');
                            }, 0);
                          }
                          // Liability Premium: Use finalPremiumAmount from the matching risk in liabilityList[].policyRiskList
                          let liabilityPremium = 0;
                          if (Array.isArray(liability.liabilityList)) {
                            for (const liabilityItem of liability.liabilityList) {
                              if (Array.isArray(liabilityItem.policyRiskList)) {
                                const match = liabilityItem.policyRiskList.find((r: any) => r.description === policyRisk.description);
                                if (match) {
                                  liabilityPremium = parseFloat(match.finalPremiumAmount || '0');
                                  break;
                                }
                              }
                            }
                          }
                          // Liability Coverage: Sum covPremiumAmt from matching risk in liabilityList[].policyRiskList
                          let liabilityCoverage = 0;
                          if (Array.isArray(liability.liabilityList)) {
                            for (const liabilityItem of liability.liabilityList) {
                              if (Array.isArray(liabilityItem.policyRiskList)) {
                                const match = liabilityItem.policyRiskList.find((r: any) => r.description === policyRisk.description);
                                if (match && Array.isArray(match.policyCoverageList)) {
                                  liabilityCoverage = match.policyCoverageList.reduce((sum: number, coverage: any) => {
                                    return sum + parseFloat(coverage.covPremiumAmt || '0');
                                  }, 0);
                                  break;
                                }
                              }
                            }
                          }
                          return {
                            riskName: policyRisk.description,
                            propertyPremium,
                            liabilityPremium,
                            totalPremium: propertyPremium + liabilityPremium, // New combined premium field
                            _policyRisk: policyRisk, // for details icon
                            _rowIndex: rowIdx,
                          };
                        });
                        // Calculate totals
                        type RowType = { propertyPremium: number; propertyCoverage: number; liabilityPremium: number; liabilityCoverage: number; totalPremium: number };
                        const total = rows.reduce(
                          (acc: RowType, row: any) => {
                            acc.propertyPremium += row.propertyPremium;
                            acc.liabilityPremium += row.liabilityPremium;
                            acc.totalPremium += row.totalPremium;
                            return acc;
                          },
                          { propertyPremium: 0, propertyCoverage: 0, liabilityPremium: 0, liabilityCoverage: 0, totalPremium: 0 }
                        );
                        // Add total row
                        rows.push({
                          riskName: t('Total'),
                          propertyPremium: total.propertyPremium,
                          liabilityPremium: total.liabilityPremium,
                          totalPremium: total.totalPremium,
                        });
                        // Table columns
                        const columns: IColumn[] = [
                          { 
                            label: t('Risk Name'), 
                            accessorKey: 'riskName', 
                            styles: { textAlign: 'left' as const },
                            Cell: (row: any) => (
                              <Box sx={RiskAndCoverageStyles.riskNameCellBox}>
                                <span>{row.riskName}</span>
                              </Box>
                            )
                          },
                          { 
                            label: t('Property Premium'), 
                            accessorKey: 'propertyPremium', 
                            styles: { textAlign: 'right' as const },
                            Cell: (row: any) => (
                              <Box sx={RiskAndCoverageStyles.rightAlignedCell}>
                                {row.riskName === t('Total') ? <b>{currencyFormat('us', row.propertyPremium)}</b> : currencyFormat('us', row.propertyPremium)}
                              </Box>
                            )
                          },
                          ...(liability?.lobCode !== 'BOP' ? [
                            { 
                              label: t('Property Coverage'), 
                              accessorKey: 'propertyCoverage', 
                              styles: { textAlign: 'right' as const },
                              Cell: (row: any) => (
                                <Box sx={RiskAndCoverageStyles.rightAlignedCell}>
                                  {row.riskName === t('Total') ? <b>{currencyFormat('us', row.propertyCoverage)}</b> : currencyFormat('us', row.propertyCoverage)}
                                </Box>
                              )
                            }
                          ] : []),
                          { 
                            label: t('Liability Premium'), 
                            accessorKey: 'liabilityPremium', 
                            styles: { textAlign: 'right' as const },
                            Cell: (row: any) => (
                              <Box sx={RiskAndCoverageStyles.rightAlignedCell}>
                                {row.riskName === t('Total') ? <b>{currencyFormat('us', row.liabilityPremium)}</b> : currencyFormat('us', row.liabilityPremium)}
                              </Box>
                            )
                          },
                          ...(liability?.lobCode !== 'BOP' ? [
                            { 
                              label: t('Liability Coverage'), 
                              accessorKey: 'liabilityCoverage', 
                              styles: { textAlign: 'right' as const },
                              Cell: (row: any) => (
                                <Box sx={RiskAndCoverageStyles.rightAlignedCell}>
                                  {row.riskName === t('Total') ? <b>{currencyFormat('us', row.liabilityCoverage)}</b> : currencyFormat('us', row.liabilityCoverage)}
                                </Box>
                              )
                            }
                          ] : []),
                          { 
                            label: t('Total Premium'), 
                            accessorKey: 'totalPremium', 
                            styles: { textAlign: 'right' as const },
                            Cell: (row: any) => (
                              <Box sx={RiskAndCoverageStyles.rightAlignedCell}>
                                {row.riskName === t('Total') ? <b>{currencyFormat('us', row.totalPremium)}</b> : currencyFormat('us', row.totalPremium)}
                              </Box>
                            )
                          },
                          { label: '', accessorKey: 'details',
                            Cell: (row: any, opts?: { rowIndex: number; isHovered: boolean }) => (
                              row.riskName !== t('Total') ? (
                                <IconButton
                                  size="small"
                                  sx={opts?.isHovered ? RiskAndCoverageStyles.detailsIconButtonVisible : RiskAndCoverageStyles.detailsIconButtonHidden}
                                  onClick={e => {
                                    e.stopPropagation();
                                    setSelectedRisk(row._policyRisk);
                                    setDrawerOpen(true);
                                  }}
                                  tabIndex={-1}
                                >
                                  <ArticleOutlinedIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            )
                          },
                        ];
                        // Render the table using the prepared rows and columns
                        return (
                          <Box sx={RiskAndCoverageStyles.tableContainerBox}>
                            <CustomTable columns={columns} data={rows} pagination={false} stickyHeader={true} />
                          </Box>
                        );
                      })()
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )
        )}
        {/* Fallback if no data */}
        {!liability && (
          <Card sx={RiskAndCoverageStyles.messageCard}>
            <Typography sx={{ color: "GrayText" }}>
              {message || t("No Policy Risks found")}
            </Typography>
          </Card>
        )}
      </Box>

      {/* CustomDrawer for Risk Details */}
      <CustomDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('Risk Details')}
        content={renderDrawerContent()}
        drawerStyles={{
          "& .MuiDrawer-paper": {
            width: "800px",
            "@media (max-width: 768px)": { width: "100%" },
          },
        }}
        contentStyles={{
          padding: 0,
          height: '100%',
        }}
      />
    </>
  );
};

export default RiskAndCoverage;
